/**
 * The dialog service: a promise on one side, a stack of requests on the other.
 *
 * Headless on purpose. The POS renders its dialogs with Tailwind and the dashboard with MUI, and
 * forcing one renderer on both would mean either Tailwind in the dashboard or a hand-built modal
 * in a codebase that already has one. What *is* shared is the part that must not differ: the
 * promise contract, the stack, and the rule that a destructive question does not close on a
 * backdrop tap.
 *
 * Focus trapping, scroll locking and focus restoration live in `useDialogHost`, because they need
 * the DOM node the renderer owns.
 */

import { uuidv7 } from '../lib/uuid.js';
import type { AlertOptions, ConfirmOptions, DialogApi, DialogRequest, PromptOptions } from './types.js';

type Listener = (stack: readonly DialogRequest[]) => void;

let stack: readonly DialogRequest[] = [];
const listeners = new Set<Listener>();

function publish(): void {
  const snapshot = stack;
  for (const listener of listeners) listener(snapshot);
}

function push(request: DialogRequest): void {
  stack = [...stack, request];
  publish();
}

function remove(id: string): void {
  stack = stack.filter((request) => request.id !== id);
  publish();
}

/** Subscribed to by the host component. Returns the unsubscribe. */
export function subscribeToDialogs(listener: Listener): () => void {
  listeners.add(listener);
  listener(stack);
  return () => {
    listeners.delete(listener);
  };
}

export const getDialogStack = (): readonly DialogRequest[] => stack;

/**
 * Settles one dialog. Idempotent: a renderer that fires both a backdrop click and an Escape
 * handler in the same frame must not reject the second call, and the promise resolves once.
 */
export function settle(id: string, value: boolean | string | null): void {
  const request = stack.find((entry) => entry.id === id);
  if (!request) return;

  remove(id);

  switch (request.kind) {
    case 'confirm':
      request.resolve(value === true);
      break;
    case 'alert':
      request.resolve(true);
      break;
    case 'prompt':
      request.resolve(typeof value === 'string' ? value : null);
      break;
  }
}

export const dialog: DialogApi = {
  confirm: (options: ConfirmOptions) =>
    new Promise<boolean>((resolve) => {
      push({
        kind: 'confirm',
        id: uuidv7(),
        options: {
          confirmLabel: 'Confirm',
          cancelLabel: 'Cancel',
          tone: 'neutral',
          // A destructive question stays open until it is answered deliberately.
          dismissOnBackdrop: options.tone !== 'danger',
          ...options,
        },
        resolve,
      });
    }),

  alert: (options: AlertOptions) =>
    new Promise<void>((resolve) => {
      push({
        kind: 'alert',
        id: uuidv7(),
        options: { confirmLabel: 'OK', tone: 'neutral', ...options },
        resolve: () => {
          resolve();
        },
      });
    }),

  prompt: (options: PromptOptions) =>
    new Promise<string | null>((resolve) => {
      push({
        kind: 'prompt',
        id: uuidv7(),
        options: { confirmLabel: 'Save', cancelLabel: 'Cancel', tone: 'neutral', ...options },
        resolve,
      });
    }),

  dismissAll: () => {
    const open = stack;
    stack = [];
    publish();

    // Resolved rather than left hanging: an awaited dialog that never settles is a leaked
    // promise and, in the sale flow, a cart stuck mid-submission.
    for (const request of open) {
      if (request.kind === 'confirm') request.resolve(false);
      else if (request.kind === 'alert') request.resolve(true);
      else request.resolve(null);
    }
  },
};
